// Admin API client for the signing-keys / trust-bundle surface
// (item 39 of PRODUCT-ROADMAP.md, "AdminTrust-Page nachreichen").
//
// The backend ships the CRUD surface in
// `api/internal/handlers/signing.go` since v9 / migration 016.
// AdminTrust is a thin operator UI on top of those endpoints — no new
// backend routes were added when the page landed.

import useSWR from 'swr';
import { parseErrorResponse } from './problem';

export type TrustAlgorithm = 'ed25519' | 'rsa-4096' | 'openpgp-rsa-4096';

export interface TrustKey {
  id: string;
  repo_id?: string | null;
  purpose: string;
  algorithm: TrustAlgorithm;
  fingerprint: string;
  trusted_for_verify: boolean;
  created_by: string;
  created_at: string;
}

export interface TrustKeyCreateInput {
  purpose: string;
  algorithm: TrustAlgorithm;
  repo_id?: string;
  trusted_for_verify?: boolean;
}

export interface TrustKeyCreateResponse {
  id: string;
  purpose: string;
  algorithm: TrustAlgorithm;
  fingerprint: string;
  trusted_for_verify: boolean;
}

const json = async (url: string) => {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw await parseErrorResponse(r);
  return r.json();
};

// useTrustKeys lists every active signing key. The same row can serve
// signing-on-publish and verify-on-pull; the page filters by purpose
// and trust-flag client-side.
export function useTrustKeys() {
  return useSWR<TrustKey[]>('/api/admin/signing/keys', json);
}

export async function createTrustKey(
  input: TrustKeyCreateInput,
): Promise<TrustKeyCreateResponse> {
  const res = await fetch('/api/admin/signing/keys', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}

export async function setTrustFlag(
  id: string,
  trusted: boolean,
): Promise<void> {
  const res = await fetch(
    `/api/admin/signing/keys/${encodeURIComponent(id)}/trust`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ trusted_for_verify: trusted }),
    },
  );
  if (!res.ok && res.status !== 204)
    throw await parseErrorResponse(res);
}

export async function retireTrustKey(id: string): Promise<void> {
  const res = await fetch(
    `/api/admin/signing/keys/${encodeURIComponent(id)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!res.ok && res.status !== 204)
    throw await parseErrorResponse(res);
}

// publicKeyURL builds the GET-able URL for a row's exported public
// material. The handler returns OpenPGP-armored bytes for RSA-OpenPGP
// keys and PEM SubjectPublicKeyInfo for Ed25519 / raw RSA.
export function publicKeyURL(purpose: string, repoID?: string | null): string {
  const base = `/api/admin/signing/keys/${encodeURIComponent(purpose)}.pub`;
  if (repoID) return `${base}?repo_id=${encodeURIComponent(repoID)}`;
  return base;
}
